import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import { useEquipmentsByFilter, useVehicleMileageLogs } from "presentation/hooks/use-query-equipments";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    filterEquipmentsByVehicleType,
    getVehicleEquipmentType,
} from "presentation/components/Equipment/equipment-utils";
import VehiclesSkeleton from "./Skeleton";
import RegisterKMs from "./components/RegisterKMs";
import { Equipment } from "domain/entities/equipment";
import { ConfirmDialog } from "primereact/confirmdialog";
import KMHistory from "./components/KMHistory";
import MyEquipmentsTab from "presentation/components/Equipment/MyEquipmentsTab";
import EquipmentDetails from "presentation/mobile/components/Equipment/EquipmentDetails";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import { MobileActionButton } from "presentation/mobile/components/MobileActionButton";
import MobileToast from "presentation/mobile/components/MobileToast";
import { EQUIPMENT_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

const FUNCTION = "vehicles";
const REQUIRE_FUNCTION = "vehicles";

const statusLabelMap: Record<string, string> = {
    IN_USE: "Em uso",
    IN_MAINTENANCE: "Em manutenção",
    IN_STOCK: "Em stock",
    LOST: "Perdido",
    OBSOLETE: "Obsoleto",
};

const Vehicles = () => {
    const { isLoading, hasPermission } = usePermissionCheck({ requiredFunction: REQUIRE_FUNCTION });

    const navigationState = useNavigationStore();
    const { userEmployee } = useAuthStore();
    const toast = useRef<Toast>(null);

    const [registerVisible, setRegisterVisible] = useState<boolean>(false);
    const [historyVisible, setHistoryVisible] = useState<boolean>(false);
    const [detailsVisible, setDetailsVisible] = useState<boolean>(false);
    const [currentEquipment, setCurrentEquipment] = useState<Equipment | null>(null);

    const {
        data: equipmentTypes,
        isLoading: isLoadingTypes,
        isError: isErrorTypes,
    } = useEquipmentTypeByFilterAll({ limit: 100 });

    const { data: mileageLogsData, isLoading: isLoadingLogs } =
        useVehicleMileageLogs(
            currentEquipment?.id,
            (historyVisible || registerVisible) && !!currentEquipment?.id,
        );

    const {
        data: equipmentsResponse,
        isLoading: isLoadingEquipments,
        isError,
    } = useEquipmentsByFilter(
        {
            employeeId: userEmployee?.id,
            limit: 100,
        },
        !!userEmployee?.id,
    );

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }
    }, [navigationState]);



    const vehicleType = useMemo(
        () => getVehicleEquipmentType(equipmentTypes?.data),
        [equipmentTypes?.data],
    );

    const vehicles = useMemo(
        () =>
            filterEquipmentsByVehicleType(
                equipmentsResponse?.data?.data,
                vehicleType?.id,
                true,
            ),
        [equipmentsResponse?.data?.data, vehicleType?.id],
    );

    const handleRegisterMileage = (equipment: Equipment) => {
        if (!equipment) {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro dos dados o equipamento (ID em falta)',
            })
            return;
        }
        setCurrentEquipment(equipment)
        setRegisterVisible(true);
    };

    const handleSeeHistory = (equipment: Equipment) => {
        if (!equipment) {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro dos dados o equipamento (ID em falta)',
            })
            return;
        }
        setCurrentEquipment(equipment)
        setHistoryVisible(true);
    }

    const handleSeeDetails = (equipment: Equipment) => {
        if (!equipment) {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro dos dados o equipamento (ID em falta)',
            })
            return;
        }
        setCurrentEquipment(equipment)
        setDetailsVisible(true);
    }

    const getOptions = (vehicle: Equipment) => {
        return (
            <div className="w-full flex justify-content-end align-items-center gap-2 pt-2" style={{ borderTop: "1px solid #F1F5F9" }}>
                <MobileActionButton
                    label="Detalhes"
                    icon="pi pi-eye"
                    severity="secondary"
                    onClick={() => handleSeeDetails(vehicle)}
                />
                <MobileActionButton
                    label="Histórico"
                    icon="pi pi-history"
                    severity="secondary"
                    onClick={() => handleSeeHistory(vehicle)}
                />
                <MobileActionButton
                    label="Registar KMs"
                    icon="pi pi-plus"
                    severity="primary"
                    onClick={() => handleRegisterMileage(vehicle)}
                />
            </div>
        );
    };

    if (isLoadingEquipments || isLoadingTypes) {
        return <VehiclesSkeleton />
    }

    return (
        <div className="flex flex-column pt-5 px-3 gap-3 pb-6 align-items-center h-full">
            <MobileToast ref={toast} />

            {isError || isErrorTypes ? (
                <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                    <i className="pi pi-exclamation-circle text-4xl text-red-500" />
                    <span className="text-sm text-center text-600">
                        Não foi possível carregar as viaturas.
                    </span>
                </div>
            ) : vehicles.length === 0 ? (
                <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                    <i className="pi pi-car text-4xl" style={{ color: "#c5c5c5" }} />
                    <span className="text-sm text-center" style={{ color: "#888" }}>
                        Não tem viaturas atribuídas.
                    </span>
                </div>
            ) : (
                <div className="w-full flex flex-column justify-content-center gap-3">
                    {vehicles.map((vehicle) => {
                        const statusLabel = statusLabelMap[vehicle.status] ?? vehicle.status;
                        const statusStyle = getStatusStyle(EQUIPMENT_STATUS_TONES, vehicle.status);

                        return (
                            <div
                                key={vehicle.id}
                                className="card-mobile p-3 flex flex-column gap-2"
                            >
                                {/* Linha 1: Marca/Modelo + Badge de Estado Squircle */}
                                <div className="w-full flex justify-content-between align-items-center">
                                    <span className="text-base font-bold text-900">
                                        {`${vehicle.brand || "-"} ${vehicle.model || "-"}`}
                                    </span>
                                    <span
                                        className="badge-squircle"
                                        style={{
                                            color: statusStyle.color,
                                            backgroundColor: statusStyle.bg,
                                            border: `1px solid ${statusStyle.border}`,

                                        }}
                                    >
                                        {statusLabel}
                                    </span>
                                </div>

                                {/* Linha 2: Matrícula com Ícone */}
                                <div className="w-full flex align-items-center gap-2">
                                    <i className="pi pi-car text-xs" style={{ color: "#94A3B8" }} />
                                    <span className="text-xs font-medium text-600">
                                        Matrícula: {vehicle.serialOrCarRegistration || "-"}
                                    </span>
                                </div>

                                {/* Linha 3: Ações Padronizadas */}
                                {getOptions(vehicle)}
                            </div>
                        );
                    })}
                </div>
            )}

            <RegisterKMs
                visible={registerVisible}
                setIsVisible={setRegisterVisible}
                equipment={currentEquipment}
                setEquipment={setCurrentEquipment}
                toastRef={toast}
                mileageLogsData={mileageLogsData}
            />
            <KMHistory
                visible={historyVisible}
                setVisible={setHistoryVisible}
                mileageLogsData={mileageLogsData}
                isLoading={isLoadingLogs}
            />
            <EquipmentDetails
                visible={detailsVisible}
                setVisible={setDetailsVisible}
                toastRef={toast}
                equipment={currentEquipment}
                setEquipment={setCurrentEquipment}
            />
            <ConfirmDialog />
        </div>
    );
};

export default Vehicles;